import { Injectable, Inject, Optional } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor } from '@angular/common/http';
import { BehaviorSubject, Observable, from, throwError } from 'rxjs';
import { mergeMap, filter, take, switchMap, finalize, catchError } from 'rxjs/operators';

import { OAuthService } from 'angular-oauth2-oidc';
import { GlobalStateService } from '@app/core/services/global-state.service';
import { OAuthRouteClassifierService } from '@app/core/services/oauth-route-classifier.service';
import { environment } from 'environments/environment';
import { PlatformBrowserService } from '@app/core/services/platform-browser.service';
import { pathsSaldo } from 'environments/paths-saldo';
import { BREADCRUMB_TRACKER_PORT } from '@app/exceptions/reporting/breadcrumbs/breadcrumb-tracker.token';
import { IBreadcrumbTrackerService } from '@app/common/observability/domain/services/breadcrumb-tracker.service';
import { getErrorReporterMetadata } from '@app/common/observability/helpers/error-reporter-metadata.helper';

@Injectable()
export class OAuthInterceptor implements HttpInterceptor {
    private refreshing: boolean = false;
    private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);
    private currentRefreshRequestId: string = '';
    private currentRefreshAttempt: number = 0;

    public constructor(
        private oAuthService: OAuthService,
        private browserService: PlatformBrowserService,
        private globalStateService: GlobalStateService,
        private routeClassifier: OAuthRouteClassifierService,
        @Optional()
        @Inject(BREADCRUMB_TRACKER_PORT)
        private breadcrumbTrackerService?: IBreadcrumbTrackerService,
    ) {
        this.oAuthService.tokenEndpoint = environment.API_URL + pathsSaldo.AUTH_PATH + 'token';
        this.oAuthService.clientId = 'saldo';
        this.oAuthService.scope = 'openid profile email voucher';
        if (this.browserService.isBrowser) {
            this.oAuthService.setStorage(localStorage);
        }
    }

    public intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (this.routeClassifier.requiresTokenRefresh(request.url)) {
            let expiration: number = this.oAuthService.getAccessTokenExpiration();
            let actual_time: number = new Date().getTime();
            let accessTokenLifetime: number = environment.ACCESS_TOKEN_LIFETIME * 1000;

            if (expiration && expiration - actual_time < accessTokenLifetime) {
                if (this.refreshing) {
                    this.breadcrumbTrackerService?.tokenRefreshRequestQueued({
                        requestId: this.currentRefreshRequestId || 'unknown',
                        attempt: this.currentRefreshAttempt,
                        endpoint: '/auth/v1/token',
                        method: 'POST',
                    });

                    return this.refreshTokenSubject.pipe(
                        filter((result): boolean => result !== null),
                        take(1),
                        switchMap(
                            (): Observable<HttpEvent<any>> =>
                                next.handle(this.addAuthenticationToken(request)),
                        ),
                    );
                }

                this.refreshing = true;
                this.refreshTokenSubject.next(null);
                const requestId: string = `token-refresh-${Date.now()}-${Math.random()
                    .toString(36)
                    .slice(2, 8)}`;
                const attempt: number = (this.currentRefreshAttempt += 1);
                const requestStartedAt: number = Date.now();
                this.currentRefreshRequestId = requestId;

                this.breadcrumbTrackerService?.tokenRefreshStarted({
                    requestId,
                    attempt,
                    endpoint: '/auth/v1/token',
                    method: 'POST',
                });

                return from(this.oAuthService.refreshToken()).pipe(
                    mergeMap((data: { [key: string]: any }): Observable<HttpEvent<any>> => {
                        this.globalStateService.setToken(data.access_token);
                        this.refreshTokenSubject.next(data.access_token);
                        this.refreshing = false;

                        this.breadcrumbTrackerService?.tokenRefreshSucceeded({
                            requestId,
                            attempt,
                            endpoint: '/auth/v1/token',
                            method: 'POST',
                            durationMs: Date.now() - requestStartedAt,
                        });

                        return next.handle(this.addAuthenticationToken(request));
                    }),
                    catchError((error: unknown): Observable<never> => {
                        this.breadcrumbTrackerService?.tokenRefreshFailed({
                            requestId,
                            attempt,
                            endpoint: '/auth/v1/token',
                            method: 'POST',
                            durationMs: Date.now() - requestStartedAt,
                            ...getErrorReporterMetadata(error),
                        });

                        return throwError(() => error);
                    }),
                    finalize((): void => {
                        this.refreshing = false;
                        this.currentRefreshRequestId = '';
                        this.currentRefreshAttempt = 0;
                    }),
                );
            }
        }

        return next.handle(request);
    }

    public addAuthenticationToken(request: HttpRequest<any>): HttpRequest<any> {
        const accessToken: string = this.oAuthService.getAccessToken();

        if (!accessToken) {
            return request;
        }

        return request.clone({
            setHeaders: {
                Authorization: `Bearer ${this.oAuthService.getAccessToken()}`,
            },
        });
    }
}
